import React, { useState, useEffect } from 'react';
import { Modal, Descriptions, Table, Tag, Space, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { InboxOutlined } from '@ant-design/icons';
import {
  CustomAttributesTags,
  hasVisibleCustomAttributes,
} from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';

interface ShelfInventory {
  id?: string;
  shelf_location?: string;
  shelf_type_name?: string | null;
  shelf_role?: string;
  quantity?: number;
  [key: string]: unknown;
}

interface InventoryDetail {
  id?: string;
  product_sku?: string;
  product_name?: string;
  inventory_type?: string;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  quantity?: number | null;
  available_quantity?: number | null;
  allocated_quantity?: number | null;
  held_quantity?: number | null;
  pending_putaway_quantity?: number | null;
  status?: string;
  custom_attributes?: Record<string, unknown> | null;
  shelf_inventories?: ShelfInventory[];
  inventory?: {
    product_sku?: string;
    product_name?: string;
    inventory_type_name?: string;
    batch?: string | null;
    expiration_date?: string | null;
    manufacturing_date?: string | null;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface InventoryServiceLike {
  getShelfInventories?: (id: string) => Promise<{
    success: boolean;
    data: ShelfInventory[] | { list?: ShelfInventory[] } | null;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

interface InventoryDetailModalProps {
  visible: boolean;
  inventory: InventoryDetail | null | undefined;
  inventoryService?: InventoryServiceLike;
  attributeLabelByName?: Record<string, string>;
  onCancel: () => void;
}

/**
 * 庫存詳情 Modal
 * 顯示庫存基本資訊 + 儲位分佈
 *
 * @param inventory - 庫存物件（來自列表 row 或 API response）
 * @param inventoryService - 用於 fetch shelf_inventories（如果 inventory 沒有內嵌）
 */
const InventoryDetailModal: React.FC<InventoryDetailModalProps> = ({ visible, inventory, inventoryService, attributeLabelByName, onCancel }) => {
  const { t } = useTranslation();
  const [shelves, setShelves] = useState<ShelfInventory[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && inventory) {
      if (inventory.shelf_inventories) {
        // 已有內嵌資料（來自 get_records response）
        setShelves(inventory.shelf_inventories);
      } else if (inventoryService?.getShelfInventories) {
        // 需要額外 fetch
        fetchShelves();
      }
    } else {
      setShelves([]);
    }
  }, [visible, inventory]);

  const fetchShelves = async () => {
    setLoading(true);
    try {
      const response = await inventoryService!.getShelfInventories!(inventory!.id as string);
      if (response.success) {
        const data = response.data;
        setShelves(Array.isArray(data) ? data : ((data as { list?: ShelfInventory[] } | null)?.list || []));
      }
    } catch (error) {
      console.error('Failed to fetch shelf inventories:', error);
      message.error(t('common.message.fetch_failed') as string);
    } finally {
      setLoading(false);
    }
  };

  if (!inventory) return null;

  const statusColors: Record<string, string> = { in_stock: 'success', out_of_stock: 'default', blocked: 'error' };

  const shelfColumns = [
    {
      title: t('models.shelf.location'),
      dataIndex: 'shelf_location',
      key: 'shelf_location',
      render: (text: string | undefined, record: ShelfInventory) => (
        <Space>
          <span>{text}</span>
          {record.shelf_role && record.shelf_role !== 'storage' && (
            <Tag color={record.shelf_role === 'staging' ? 'orange' : 'blue'}>
              {t(`enums.shelf_role.${record.shelf_role}`)}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('models.shelf.shelf_type'),
      dataIndex: 'shelf_type_name',
      key: 'shelf_type_name',
      render: (text: string | null | undefined) => text || '-',
    },
    {
      title: t('models.inventory.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right' as const,
    },
  ];

  const customAttributes = inventory.custom_attributes;
  const hasCustomAttributes = hasVisibleCustomAttributes(customAttributes);

  return (
    <Modal
      title={
        <Space>
          <InboxOutlined style={{ color: '#1890ff' }} />
          {inventory.product_sku || inventory.inventory?.product_sku} - {t('shared.inventory_detail.title')}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('models.inventory.product')} span={2}>
          <div>{inventory.product_sku || inventory.inventory?.product_sku}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{inventory.product_name || inventory.inventory?.product_name}</div>
        </Descriptions.Item>
        <Descriptions.Item label={t('models.inventory_type._')}>
          {inventory.inventory_type || inventory.inventory?.inventory_type_name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.inventory.batch')}>
          {inventory.batch || inventory.inventory?.batch || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.inventory.expiration_date')}>
          {inventory.expiration_date || inventory.inventory?.expiration_date || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.inventory.manufacturing_date')}>
          {inventory.manufacturing_date || inventory.inventory?.manufacturing_date || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.inventory.quantity')}>
          {inventory.quantity ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.inventory.available_quantity')}>
          {inventory.available_quantity ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.inventory.allocated_quantity')}>
          {inventory.allocated_quantity ?? 0}
        </Descriptions.Item>
        <Descriptions.Item label={t('models.inventory.held_quantity')}>
          {(inventory.held_quantity || 0) > 0
            ? <Tag color="warning">{inventory.held_quantity}</Tag>
            : 0
          }
        </Descriptions.Item>
        {(inventory.pending_putaway_quantity || 0) > 0 && (
          <Descriptions.Item label={t('models.inventory.pending_putaway_quantity')}>
            <Tag color="orange">{inventory.pending_putaway_quantity}</Tag>
          </Descriptions.Item>
        )}
        {inventory.status && (
          <Descriptions.Item label={t('models.inventory.status')}>
            <Tag color={statusColors[inventory.status] || 'default'}>
              {t(`enums.inventory.status.${inventory.status}`)}
            </Tag>
          </Descriptions.Item>
        )}
        {hasCustomAttributes && (
          <Descriptions.Item label={t('models.inventory.custom_attributes')} span={2}>
            <CustomAttributesTags attributes={customAttributes} attributeLabelByName={attributeLabelByName} emptyFallback={null} />
          </Descriptions.Item>
        )}
      </Descriptions>

      <Table
        columns={shelfColumns}
        dataSource={shelves}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        locale={{ emptyText: t('shared.inventory_detail.no_shelves') }}
      />
    </Modal>
  );
};

export default InventoryDetailModal;
